import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Loader2, ArrowRight, Phone } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "bot",
      text: "Hey! Welcome to Afro Magic Braiding Hair. Ask me about our styles, prices, or book an appointment. How can I help you today?",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `chat-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const [collectingPhone, setCollectingPhone] = useState(false);
  const [pendingQuestion, setPendingQuestion] = useState("");
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const scrollToBooking = () => {
    setOpen(false);
    const bookingSection = document.getElementById("booking");
    if (bookingSection) {
      bookingSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput("");
    setMessages((prev) => [...prev, { role: "user", text }]);
    setLoading(true);

    try {
      // If we're collecting a phone number
      if (collectingPhone) {
        const phoneMatch = text.match(/[\d\-\(\)\+\s]{7,}/);
        if (phoneMatch) {
          const phone = phoneMatch[0].trim();
          const res = await axios.post(`${API}/chat`, {
            message: `[PHONE_COLLECTED]|${phone}|${pendingQuestion}`,
            session_id: sessionId,
          });
          setMessages((prev) => [
            ...prev,
            { role: "bot", text: res.data.response, action: res.data.action, whatsapp_link: res.data.whatsapp_link },
          ]);
          setCollectingPhone(false);
          setPendingQuestion("");
        } else {
          setMessages((prev) => [
            ...prev,
            { role: "bot", text: "I didn't catch a phone number. Could you please share your phone number so our stylist can reach out?" },
          ]);
        }
        setLoading(false);
        return;
      }

      const res = await axios.post(`${API}/chat`, {
        message: text,
        session_id: sessionId,
      });

      const botMsg = {
        role: "bot",
        text: res.data.response,
        action: res.data.action,
      };
      setMessages((prev) => [...prev, botMsg]);

      // Detect if bot is asking for phone number (from AI response or explicit action)
      if (res.data.action === "collect_phone" || 
          res.data.response.toLowerCase().includes("phone number") ||
          res.data.response.toLowerCase().includes("share your number")) {
        setCollectingPhone(true);
        setPendingQuestion(text);
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "bot", text: "Oops, something went wrong. Please try again or call us at (313) 484-7307!" },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-widget-toggle"
          className="fixed bottom-20 right-4 z-[9998] w-14 h-14 bg-brand-gold text-black rounded-full flex items-center justify-center shadow-lg hover:scale-105 transition-transform"
        >
          <MessageCircle size={24} />
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div
          className="fixed bottom-20 right-4 z-[9998] w-[360px] max-w-[calc(100vw-32px)] h-[520px] max-h-[calc(100vh-120px)] bg-brand-bg border border-brand-border rounded-lg shadow-2xl flex flex-col overflow-hidden"
          data-testid="chat-widget-panel"
        >
          {/* Header */}
          <div className="bg-brand-surface border-b border-brand-border px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-brand-gold rounded-full flex items-center justify-center">
                <MessageCircle size={16} className="text-black" />
              </div>
              <div>
                <p className="font-sans text-sm font-semibold text-brand-text">Afro Magic Assistant</p>
                <p className="font-sans text-[10px] text-brand-gold">Online now</p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="chat-widget-close"
              className="text-brand-text-muted hover:text-brand-text transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="chat-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-lg font-sans text-sm leading-relaxed ${
                    msg.role === "user"
                      ? "bg-brand-gold text-black rounded-br-none"
                      : "bg-brand-surface border border-brand-border text-brand-text rounded-bl-none"
                  }`}
                  data-testid={`chat-msg-${i}`}
                >
                  {msg.text}
                  {msg.action === "book_now" && (
                    <button
                      onClick={scrollToBooking}
                      data-testid="chat-book-now-btn"
                      className="mt-2 flex items-center gap-1.5 px-3 py-1.5 bg-brand-gold text-black rounded font-semibold text-xs hover:bg-brand-gold-light transition-colors w-full justify-center"
                    >
                      <ArrowRight size={14} /> Book Appointment Now
                    </button>
                  )}
                  {msg.action === "phone_collected" && msg.whatsapp_link && (
                    <a
                      href={msg.whatsapp_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="chat-whatsapp-btn"
                      className="mt-2 flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded font-semibold text-xs hover:bg-green-500 transition-colors w-full justify-center"
                    >
                      <Phone size={14} /> WhatsApp Us
                    </a>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-brand-surface border border-brand-border px-3 py-2 rounded-lg rounded-bl-none">
                  <Loader2 size={16} className="animate-spin text-brand-gold" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-brand-border px-3 py-2 flex items-center gap-2 flex-shrink-0 bg-brand-surface">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={collectingPhone ? "Enter your phone number..." : "Ask about styles, prices..."}
              data-testid="chat-input"
              className="flex-1 bg-brand-bg border border-brand-border text-brand-text placeholder:text-brand-text-muted/50 px-3 py-2 rounded font-sans text-sm focus:outline-none focus:border-brand-gold"
            />
            <button
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              data-testid="chat-send-btn"
              className="w-9 h-9 bg-brand-gold text-black rounded flex items-center justify-center hover:bg-brand-gold-light transition-colors disabled:opacity-40"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
